import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from 'react'

type ToastType = 'success' | 'error' | 'info'

interface Toast {
  id: number
  type: ToastType
  message: string
  exiting: boolean
}

interface ToastContextValue {
  toast: (opts: { type: ToastType; message: string }) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

const MAX_TOASTS = 3
const AUTO_DISMISS_MS = 4000
const EXIT_ANIMATION_MS = 300

const typeStyles: Record<ToastType, string> = {
  success: 'bg-green-600 text-white',
  error: 'bg-red-600 text-white',
  info: 'bg-blue-600 text-white',
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(0)
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())

  const removeToast = useCallback((id: number) => {
    // Start exit animation
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t))
    // Remove after animation
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, EXIT_ANIMATION_MS)
  }, [])

  const toast = useCallback(({ type, message }: { type: ToastType; message: string }) => {
    const id = nextId.current++
    setToasts(prev => {
      const next = [...prev, { id, type, message, exiting: false }]
      // Evict oldest if over max
      if (next.length > MAX_TOASTS) {
        const evicted = next[0]
        // Clear its timer
        const timer = timersRef.current.get(evicted.id)
        if (timer) { clearTimeout(timer); timersRef.current.delete(evicted.id) }
        return next.slice(1)
      }
      return next
    })
    // Auto-dismiss
    const timer = setTimeout(() => {
      timersRef.current.delete(id)
      removeToast(id)
    }, AUTO_DISMISS_MS)
    timersRef.current.set(id, timer)
  }, [removeToast])

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(t => clearTimeout(t))
    }
  }, [])

  const handleClose = useCallback((id: number) => {
    const timer = timersRef.current.get(id)
    if (timer) { clearTimeout(timer); timersRef.current.delete(id) }
    removeToast(id)
  }, [removeToast])

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/* Toast container */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
        {toasts.map(t => (
          <div
            key={t.id}
            className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium min-w-[280px] max-w-[400px] transition-all duration-300 ${typeStyles[t.type]} ${
              t.exiting
                ? 'opacity-0 translate-x-8'
                : 'opacity-100 translate-x-0 animate-[slideInRight_0.3s_ease-out]'
            }`}
          >
            <span className="flex-1">{t.message}</span>
            <button
              onClick={() => handleClose(t.id)}
              className="ml-2 opacity-70 hover:opacity-100 text-lg leading-none font-bold"
              aria-label="Fermer"
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
